import React, { useMemo, useState } from "react"
import OdontogramSelector from "./OdontogramSelector"
import { apiFetch } from "../../../utils/apiFetch"

function createEmptyItem(defaultDoctorId = "") {
  return {
    localId: crypto.randomUUID(),
    treatmentId: "",
    suggestedDoctorId: defaultDoctorId,
    assignedDoctorId: defaultDoctorId,
    tooth: null,
    surface: "ALL",
    condition: "",
    price: "",
    notes: ""
  }
}

const surfaces = [
  { value: "ALL", label: "Pieza completa" },
  { value: "O", label: "Oclusal" },
  { value: "M", label: "Mesial" },
  { value: "D", label: "Distal" },
  { value: "V", label: "Vestibular" },
  { value: "L", label: "Lingual / Palatina" }
]

const conditions = [
  "Caries",
  "Obturación",
  "Endodoncia",
  "Corona",
  "Implante",
  "Ausente",
  "Fractura",
  "Desgaste",
  "Prótesis",
  "Control",
  "Otro"
]

const fieldLabelStyle = {
  display: "block",
  marginBottom: 6,
  color: "#475569",
  fontSize: 13,
  fontWeight: 700
}

const inputStyle = {
  width: "100%",
  padding: 10,
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a"
}

export default function TreatmentPlanModal({
  open,
  onClose,
  onCreated,
  clinicId,
  patient,
  userId,
  treatments,
  doctors,
  apiBase
}) {
  const safeDoctors = Array.isArray(doctors) ? doctors : []
  const safeTreatments = Array.isArray(treatments) ? treatments : []

  const singleDoctor = safeDoctors.length === 1 ? safeDoctors[0] : null
  const defaultDoctorId = singleDoctor?.id || ""
  const showDoctorSelectors = safeDoctors.length > 1

  const [title, setTitle] = useState("Plan de tratamiento")
  const [notes, setNotes] = useState("")
  const [saving, setSaving] = useState(false)

  const [items, setItems] = useState(() => [createEmptyItem(defaultDoctorId)])

  const totalEstimated = useMemo(() => {
    return items.reduce((acc, item) => acc + Number(item.price || 0), 0)
  }, [items])

  if (!open) return null

  const addItem = () => {
    setItems((prev) => [...prev, createEmptyItem(defaultDoctorId)])
  }

  const removeItem = (localId) => {
    setItems((prev) => {
      if (prev.length === 1) return prev
      return prev.filter((item) => item.localId !== localId)
    })
  }

  const updateItem = (localId, patch) => {
    setItems((prev) =>
      prev.map((item) =>
        item.localId === localId ? { ...item, ...patch } : item
      )
    )
  }

  const handleTreatmentChange = (localId, treatmentId) => {
    const treatment = safeTreatments.find((t) => t.id === treatmentId)

    updateItem(localId, {
      treatmentId,
      price: treatment ? String(treatment.price || 0) : "",
      condition: treatment?.odontogramCondition || ""
    })
  }

  const handleSubmit = async () => {
    if (!clinicId || !patient?.id) {
      alert("Faltan datos del paciente o clínica")
      return
    }

    const normalizedItems = items.map((item) => {
      const resolvedSuggestedDoctorId =
        item.suggestedDoctorId || defaultDoctorId || undefined

      const resolvedAssignedDoctorId =
        item.assignedDoctorId || defaultDoctorId || undefined

      return {
        treatmentId: item.treatmentId,
        suggestedDoctorId: resolvedSuggestedDoctorId,
        assignedDoctorId: resolvedAssignedDoctorId,
        tooth: item.tooth || undefined,
        surface: item.surface || undefined,
        condition: item.condition || undefined,
        price: Number(item.price || 0),
        notes: item.notes || undefined
      }
    })

    if (normalizedItems.some((item) => !item.treatmentId)) {
      alert("Cada ítem del plan debe tener tratamiento")
      return
    }

    try {
      setSaving(true)

      const res = await apiFetch(`${apiBase}/appointments/treatment-plan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          patientId: patient.id,
          createdById: userId || undefined,
          title: title || "Plan de tratamiento",
          notes: notes || undefined,
          items: normalizedItems
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo crear el plan")
        return
      }

      alert("Plan de tratamiento creado correctamente")

      setTitle("Plan de tratamiento")
      setNotes("")
      setItems([createEmptyItem(defaultDoctorId)])

      onCreated?.(data)
      onClose?.()
    } catch {
      alert("Error al crear el plan")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.35)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1001005,
        padding: 20
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: 1000,
          maxWidth: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "#ffffff",
          borderRadius: 20,
          border: "1px solid #e5e7eb",
          boxShadow: "0 20px 45px rgba(15,23,42,0.18)",
          padding: 20
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            marginBottom: 18
          }}
        >
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "#0f172a" }}>
              Nuevo plan de tratamiento
            </div>
            <div style={{ fontSize: 14, color: "#64748b", marginTop: 4 }}>
              Paciente: {patient?.firstName} {patient?.lastName}
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              border: "1px solid #d1d5db",
              background: "#ffffff",
              borderRadius: 10,
              padding: "8px 12px",
              cursor: "pointer"
            }}
          >
            Cerrar
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gap: 12,
            marginBottom: 18
          }}
        >
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título del plan"
            style={{
              width: "100%",
              padding: 12,
              borderRadius: 12,
              border: "1px solid #cbd5e1"
            }}
          />

          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notas generales del plan"
            rows={3}
            style={{
              width: "100%",
              padding: 12,
              borderRadius: 12,
              border: "1px solid #cbd5e1",
              resize: "vertical"
            }}
          />
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          {items.map((item, index) => (
            <div
              key={item.localId}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                padding: 16,
                background: "#f8fafc"
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                  marginBottom: 12
                }}
              >
                <div style={{ fontWeight: 800, color: "#0f172a" }}>
                  Ítem {index + 1}
                </div>

                <button
                  onClick={() => removeItem(item.localId)}
                  style={{
                    border: "1px solid #fecaca",
                    background: "#fff1f2",
                    color: "#b91c1c",
                    borderRadius: 10,
                    padding: "8px 12px",
                    cursor: items.length === 1 ? "not-allowed" : "pointer",
                    opacity: items.length === 1 ? 0.55 : 1
                  }}
                >
                  Quitar
                </button>
              </div>

              {singleDoctor ? (
                <div
                  style={{
                    marginBottom: 12,
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: "#eef6ff",
                    border: "1px solid #bfdbfe",
                    color: "#1e3a8a",
                    fontSize: 13,
                    fontWeight: 700
                  }}
                >
                  Doctor del plan: {singleDoctor.name}
                </div>
              ) : null}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: showDoctorSelectors
                    ? "repeat(auto-fit, minmax(220px, 1fr))"
                    : "repeat(auto-fit, minmax(260px, 1fr))",
                  gap: 12
                }}
              >
                <label>
                  <span style={fieldLabelStyle}>Tratamiento</span>
                  <select
                    value={item.treatmentId}
                    onChange={(e) =>
                      handleTreatmentChange(item.localId, e.target.value)
                    }
                    style={inputStyle}
                  >
                    <option value="">Selecciona tratamiento</option>
                    {safeTreatments.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>

                {showDoctorSelectors ? (
                  <>
                    <label>
                      <span style={fieldLabelStyle}>Doctor sugerido</span>
                      <select
                        value={item.suggestedDoctorId}
                        onChange={(e) =>
                          updateItem(item.localId, {
                            suggestedDoctorId: e.target.value
                          })
                        }
                        style={inputStyle}
                      >
                        <option value="">Selecciona doctor</option>
                        {safeDoctors.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label>
                      <span style={fieldLabelStyle}>Doctor asignado</span>
                      <select
                        value={item.assignedDoctorId}
                        onChange={(e) =>
                          updateItem(item.localId, {
                            assignedDoctorId: e.target.value
                          })
                        }
                        style={inputStyle}
                      >
                        <option value="">Selecciona doctor</option>
                        {safeDoctors.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                ) : null}

                <label>
                  <span style={fieldLabelStyle}>Costo estimado</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Ej. 3500"
                    value={item.price}
                    onChange={(e) =>
                      updateItem(item.localId, { price: e.target.value })
                    }
                    style={inputStyle}
                  />
                </label>
              </div>

              <div
                style={{
                  marginTop: 14,
                  marginBottom: 8,
                  fontSize: 13,
                  color: "#64748b",
                  fontWeight: 700
                }}
              >
                Diente
              </div>

              <OdontogramSelector
                selected={item.tooth}
                onSelect={(value) => updateItem(item.localId, { tooth: value })}
              />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: 12,
                  marginTop: 12
                }}
              >
                <label>
                  <span style={fieldLabelStyle}>Superficie</span>
                  <select
                    value={item.surface}
                    onChange={(e) =>
                      updateItem(item.localId, { surface: e.target.value })
                    }
                    style={inputStyle}
                  >
                    {surfaces.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span style={fieldLabelStyle}>Condición</span>
                  <select
                    value={item.condition}
                    onChange={(e) =>
                      updateItem(item.localId, { condition: e.target.value })
                    }
                    style={inputStyle}
                  >
                    <option value="">Selecciona condición</option>
                    {conditions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <textarea
                value={item.notes}
                onChange={(e) =>
                  updateItem(item.localId, { notes: e.target.value })
                }
                placeholder="Notas del ítem"
                rows={2}
                style={{
                  width: "100%",
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid #cbd5e1",
                  resize: "vertical",
                  marginTop: 12
                }}
              />
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 16,
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center"
          }}
        >
          <button
            onClick={addItem}
            style={{
              border: "1px solid #bfdbfe",
              background: "#eff6ff",
              color: "#1d4ed8",
              borderRadius: 10,
              padding: "10px 14px",
              cursor: "pointer"
            }}
          >
            Agregar ítem
          </button>

          <div style={{ fontWeight: 800, color: "#0f172a" }}>
            Total estimado:{" "}
            {Number(totalEstimated || 0).toLocaleString("es-MX", {
              style: "currency",
              currency: "MXN"
            })}
          </div>

          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              border: "1px solid #bbf7d0",
              background: saving ? "#dcfce7" : "#f0fdf4",
              color: "#166534",
              borderRadius: 10,
              padding: "10px 14px",
              cursor: saving ? "wait" : "pointer",
              fontWeight: 800
            }}
          >
            {saving ? "Guardando..." : "Crear plan"}
          </button>
        </div>
      </div>
    </div>
  )
}